/**
 * 命令队列：串行状态机容器（pump 逻辑在 AgentCommandExecutor 中）。
 * 支持：串行、前置条件等待、超时、取消、幂等、重试计数。
 */
import type { AgentUiCommand, QueueEvent } from "@/agent/types";

export type CmdState =
  | "pending"
  | "running"
  | "success"
  | "failed"
  | "timeout"
  | "cancelled"
  | "skipped";

export interface QueuedCommand {
  command: AgentUiCommand;
  state: CmdState;
  attempts: number;
}

type QueueListener = (event: QueueEvent) => void;

export class AgentCommandQueue {
  private commands: QueuedCommand[] = [];
  private listeners: QueueListener[] = [];
  private cancelled = false;
  private answerPatch = "";
  /** 后端图状态是整轮完成状态的权威来源。 */
  private serverPlanStatus = "";
  /** 幂等：本轮已执行过的 commandId（防重复派发） */
  private executedCommandIds = new Set<string>();

  /** 入队新命令（清空旧计划） */
  enqueue(commands: AgentUiCommand[]): void {
    this.commands = (commands || [])
      .filter((command) => command && typeof command.commandId === "string")
      .map((command) => ({
        command,
        state: "pending" as CmdState,
        attempts: 0
      }));
    this.executedCommandIds.clear();
    this.cancelled = false;
    this.answerPatch = "";
    this.serverPlanStatus = "";
    this.emit({ type: "commandProgress", status: "queued", summary: `入队 ${this.commands.length} 条命令` });
  }

  cancel(reason: string): void {
    if (this.cancelled) return;
    this.cancelled = true;
    for (const item of this.commands) {
      if (item.state === "pending" || item.state === "running") {
        item.state = "cancelled";
      }
    }
    this.emit({ type: "queueCancelled", summary: reason });
  }

  isCancelled(): boolean {
    return this.cancelled;
  }

  hasCommands(): boolean {
    return this.commands.length > 0;
  }

  /** 取下一个可执行命令（pending 且未取消） */
  getNextPending(): QueuedCommand | null {
    if (this.cancelled) return null;
    for (const item of this.commands) {
      if (item.state === "pending") {
        return item;
      }
    }
    return null;
  }

  markRunning(item: QueuedCommand): void {
    item.state = "running";
    item.attempts += 1;
  }

  mark(item: QueuedCommand, state: CmdState): void {
    item.state = state;
    if (state === "success" || state === "failed" || state === "skipped" || state === "timeout" || state === "cancelled") {
      this.executedCommandIds.add(item.command.commandId);
    }
  }

  /** 重试：attempts 未达上限时回到 pending */
  resetToPending(item: QueuedCommand): boolean {
    const maxRetries = retryLimitFor(item.command);
    if (item.attempts >= maxRetries) {
      return false;
    }
    item.state = "pending";
    return true;
  }

  getExecutedIds(): ReadonlySet<string> {
    return this.executedCommandIds;
  }

  /** 查询某命令状态（用于 commandSuccess 前置条件） */
  getState(commandId: string): CmdState | null {
    for (const item of this.commands) {
      if (item.command.commandId === commandId) {
        return item.state;
      }
    }
    return null;
  }

  /** 取最近一条 renderRoute 的 commandId（供后续路线命令引用） */
  lastRenderRouteId(): string | null {
    for (let index = this.commands.length - 1; index >= 0; index -= 1) {
      const item = this.commands[index];
      if (item.command.type === "map.renderRoute") {
        return item.command.commandId;
      }
    }
    return null;
  }

  /**
   * 把后端返回的 nextCommands 追加到队尾。
   * 同一个 commandId 在队列里只允许有一条：getState() 只认第一个匹配项，
   * 出现两条同 id 命令时 commandSuccess 前置条件可能读到旧那条的状态，
   * 而两条都会被派发。所以已存在的（非 running）就地更新，不新增。
   */
  enqueueNext(commands: AgentUiCommand[]): void {
    const fresh = (commands || []).filter(
      (command) => command && typeof command.commandId === "string"
    );
    if (!fresh.length) return;
    let appended = 0;
    for (const command of fresh) {
      const queued = this.commands.find((item) => item.command.commandId === command.commandId);
      if (queued) {
        // 正在跑的那条不能动：它的回执还在路上，改了内容回执就对不上命令了。
        // 同 id 重复下发直接丢弃，等这条的回执。
        if (queued.state === "running") continue;
        // 同 id 不同 type 是替换而不是重试，重试次数重新计。
        if (queued.command.type !== command.type) queued.attempts = 0;
        queued.command = command;
        queued.state = "pending";
        continue;
      }
      this.commands.push({ command, state: "pending", attempts: 0 });
      appended += 1;
    }
    this.emit({ type: "commandProgress", status: "queued", summary: `追加 ${appended} 条命令` });
  }

  /** 覆盖当前计划的 answerPatch（由回执响应产生） */
  applyPatch(patch: { answerPatch?: string; planStatus?: string }): void {
    if (patch.planStatus) {
      this.serverPlanStatus = patch.planStatus;
    }
    if (patch.answerPatch) {
      this.answerPatch = patch.answerPatch;
      this.emit({ type: "commandProgress", status: "patched", answerPatch: patch.answerPatch });
    }
  }

  getAnswerPatch(): string {
    return this.answerPatch;
  }

  /** 由命令状态推导计划状态：completed | failed | pending */
  getPlanStatus(): string {
    if (!this.commands.length) return "completed";
    let anyFailed = false;
    let allTerminated = true;
    for (const item of this.commands) {
      switch (item.state) {
        case "success":
        case "skipped":
          break;
        case "failed":
        case "timeout":
          anyFailed = true;
          break;
        default:
          allTerminated = false;
      }
    }
    if (anyFailed) return "failed";
    if (!allTerminated) return "pending";
    return this.serverPlanStatus || "completed";
  }

  onEvent(listener: QueueListener): () => void {
    this.listeners.push(listener);
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index >= 0) this.listeners.splice(index, 1);
    };
  }

  private emit(event: QueueEvent): void {
    for (const listener of [...this.listeners]) {
      try {
        listener(event);
      } catch {
        // 监听器异常不阻断队列
      }
    }
  }
}

/** 命令重试上限：优先取命令参数，默认 2 */
export function retryLimitFor(command: AgentUiCommand): number {
  const value = command.params?.maxRetries;
  if (typeof value === "number" && Number.isFinite(value) && value >= 0) {
    return value;
  }
  return 2;
}
